import { Injectable } from '@angular/core';
import * as ngCommonHttp from '@angular/common/http';

import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AbstractOrdersService } from '@abstract-services/orders/1.0/orders.abstract-class';
import {
  CheckoutCancelStatus,
  OrderDetails,
  OrderRefunds,
  OrderSummaries,
  OrderSummariesResponse,
  OrderCancellationReason,
} from '@interfaces/orders/1.0';
import { OrdersAPI } from '@constants/api';
import { PaginationService } from '@services/pagination/1.0/pagination.service';
import { ApiResponse } from '@interfaces/api-response/1.0';

@Injectable({
  providedIn: 'root',
})
export class OrdersService implements AbstractOrdersService {
  ordersApiUrl: string;
  orderByIdApiUrl: string;
  orderCanCancelApiUrl: string;
  orderCancelApiUrl: string;
  orderRefundsApiUrl: string;
  postOrderRefundApi: string;
  putOrderProcessingStatusApi: string;
  cancelOrderApiUrl: string;

  constructor(
    private http: ngCommonHttp.HttpClient,
    private paginationService: PaginationService,
  ) {
    this.ordersApiUrl = OrdersAPI.GET;
    this.orderByIdApiUrl = OrdersAPI.GET_ORDER_BY_ID;
    this.orderCanCancelApiUrl = OrdersAPI.GET_ORDER_CAN_CANCEL;
    this.orderCancelApiUrl = OrdersAPI.POST_ORDER_CANCEL;
    this.orderRefundsApiUrl = OrdersAPI.GET_ORDER_REFUNDS;
    this.postOrderRefundApi = OrdersAPI.POST_ORDER_REFUND;
    this.putOrderProcessingStatusApi = OrdersAPI.PUT_PROCESSING_STATUS;
    this.cancelOrderApiUrl = OrdersAPI.CANCEL_ORDER;
  }

  /**
   * Gets all orders
   *
   * @returns observable of all orders
   */
  getOrderSummaries(params?: string): Observable<OrderSummaries[]> {
    const Url = params ? this.ordersApiUrl + params : OrdersAPI.GET;
    return this.http.get(`${Url}`).pipe(
      map((response: OrderSummariesResponse) => {
        if (response._embedded && response._embedded.orderSummaries.length) {
          return response._embedded.orderSummaries;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets orders also returning pagination data
   * @param params
   * @returns
   */
  getOrderSummariesPage(params?: string): Observable<ApiResponse> {
    // Ensure params starts with '?' for query string
    let Url = this.ordersApiUrl;
    if (params) {
      Url += params.startsWith('?') ? params : '?' + params;
    }
    return this.paginationService.fetchPage(Url).pipe(
      map((response: ApiResponse) => {
        if (response) {
          return response;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets order by OrderId
   *
   * @returns observable of an OrderDetails
   */
  getOrderById(orderId: string): Observable<OrderDetails> {
    const url = this.orderByIdApiUrl.replace(':orderId', orderId);
    return this.http.get(`${url}`).pipe(
      map((response: OrderDetails) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getOrderRefunds(orderId: string): Observable<OrderRefunds[]> {
    const Url = this.orderRefundsApiUrl.replace(':orderId', orderId);
    return this.http.get(`${Url}`).pipe(
      map((response: any) => {
        if (response._embedded && response._embedded.orderRefunds?.length) {
          return response._embedded.orderRefunds;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Cancels an order by OrderId
   *
   * @returns observable of an CheckoutCancelStatus
   */
  postOrderCancel(tenantId: string, orderId: string): Observable<CheckoutCancelStatus> {
    const url = this.orderCancelApiUrl.replace(':tenantId', tenantId).replace(':orderId', orderId);
    return this.http
      .post(`${url}`, {})
      .pipe(catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)));
  }

  /**
   * Refunds an order by TransactionId
   *
   * @returns observable of an OrderRefund
   */
  postOrderRefund(transactionId: string, body: any): Observable<OrderRefunds> {
    const url = this.postOrderRefundApi.replace(':transactionId', transactionId);
    return this.http
      .post(`${url}`, body)
      .pipe(catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)));
  }

  /**
   * Updates the order processing status
   * @param orderId
   * @param processingStatus
   * @returns
   */
  putOrderProcessingStatus(orderId: string, processingStatus: string): Observable<void> {
    const url = this.putOrderProcessingStatusApi.replace(':orderId', orderId);
    return this.http
      .put(`${url}`, {
        value: processingStatus,
      })
      .pipe(
        map(() => {
          return;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  cancelOrder(orderId: string, cancellationReason: OrderCancellationReason): Observable<void> {
    const url = this.cancelOrderApiUrl.replace(':orderId', orderId);
    return this.http
      .post(`${url}`, {
        cancellationReason: cancellationReason,
      })
      .pipe(
        map(() => {
          return;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }
}
